<template>
    <div
        ref="page"
        class="editor-text-page"
        :class="{ 'is-active': active === page.id }"
        :data-page="page.id"
        @mouseenter="onMouseEnter"
    >
        <template v-if="isReviewerInline">
            <div
                v-for="paragraph in reviewerParagraphs"
                :key="paragraph.startBookPartId"
                class="reviewer-paragraph"
            >
                <div class="reviewer-paragraph__personage">
                    <EditorPersonageIcon
                        v-if="paragraph.personages.length"
                        :personages="paragraph.personages"
                    />
                </div>
                <div class="reviewer-paragraph__content">
                    <template v-for="item in paragraph.items" :key="item.key || item.id">
                        <div
                            v-if="item.type === 'list-line'"
                            class="text__list-line"
                            :style="getListLineStyle(item)"
                        >
                            <template v-for="partId in item.ids" :key="partId">
                                <Sentence
                                    v-if="isListTextPart(page.bookparts[partId])"
                                    class="text__entity"
                                    :part="page.bookparts[partId]"
                                    :playing-part="playingPart"
                                    :show-style-rules="showStyleRules"
                                    @playPart="onPlayPart"
                                />
                                <Pause
                                    v-else-if="isListPausePart(page.bookparts[partId])"
                                    class="text__entity"
                                    :part="page.bookparts[partId]"
                                    :playing="playingPart && playingPart.partId === partId"
                                    :disabled="isReviewer"
                                />
                            </template>
                        </div>
                        <Sentence
                            v-else-if="showBookpart(item.id)"
                            class="text__entity"
                            :part="page.bookparts[item.id]"
                            :playing-part="playingPart"
                            :show-style-rules="showStyleRules"
                            @playPart="onPlayPart"
                        />
                        <Pause
                            v-else-if="showPause(item.id)"
                            class="text__entity"
                            :part="page.bookparts[item.id]"
                            :playing="playingPart && playingPart.partId === item.id"
                            :disabled="isReviewer"
                        />
                    </template>
                </div>
            </div>
        </template>
        <template v-else>
            <template v-for="item in renderItems" :key="item.key || item.id">
                <div
                    v-if="item.type === 'list-line'"
                    class="text__list-line"
                    :style="getListLineStyle(item)"
                >
                    <template v-for="partId in item.ids" :key="partId">
                        <Sentence
                            v-if="isListTextPart(page.bookparts[partId])"
                            class="text__entity"
                            :part="page.bookparts[partId]"
                            :playing-part="playingPart"
                            :show-style-rules="showStyleRules"
                            @playPart="onPlayPart"
                        />
                        <Pause
                            v-else-if="isListPausePart(page.bookparts[partId])"
                            class="text__entity"
                            :part="page.bookparts[partId]"
                            :playing="playingPart && playingPart.partId === partId"
                            :disabled="isReviewer"
                        />
                    </template>
                </div>
                <Sentence
                    v-else-if="showBookpart(item.id)"
                    class="text__entity"
                    :part="page.bookparts[item.id]"
                    :playing-part="playingPart"
                    :show-style-rules="showStyleRules"
                    @playPart="onPlayPart"
                />
                <Pause
                    v-else-if="showPause(item.id)"
                    class="text__entity"
                    :part="page.bookparts[item.id]"
                    :playing="playingPart && playingPart.partId === item.id"
                    :disabled="isReviewer"
                />
            </template>
        </template>
        <slot />
    </div>
</template>

<script>
import Pause from '../Pause/Pause';
import Sentence from '../Sentence/Sentence';
import EditorPersonageIcon from '../EditorPersonageIcon/EditorPersonageIcon.vue';

import { mapGetters } from 'vuex';

import {
    getListIndentStyle,
    groupListRenderItems,
    isListPausePart,
    isListTextPart,
    isPause,
} from '../../utils';

export default {
    name: 'EditorTextPage',
    components: {
        Pause,
        Sentence,
        EditorPersonageIcon,
    },
    inject: ['setActivePage', 'customerSettings'],
    props: {
        page: {
            type: Object,
            required: true,
        },
        active: {
            type: Number,
            required: true,
        },
        playingPart: {
            type: Object,
            default: () => undefined,
        },
        reviewerInlinePersonages: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            observer: undefined,
            showStyleRules: false,
        };
    },

    computed: {
        ...mapGetters(['isReviewer']),

        renderItems() {
            return groupListRenderItems(this.page.bookpartsList, this.page.bookparts);
        },

        isReviewerInline() {
            return this.reviewerInlinePersonages.length > 0;
        },

        reviewerParagraphs() {
            const paragraphs = this.reviewerInlinePersonages.map((group) => ({
                ...group,
                items: [],
            }));
            const paragraphByBookPartId = new Map();

            paragraphs.forEach((paragraph) => {
                const startIndex = this.page.bookpartsList.indexOf(paragraph.startBookPartId);
                const endIndex = this.page.bookpartsList.indexOf(paragraph.endBookPartId);

                this.page.bookpartsList.slice(startIndex, endIndex + 1).forEach((bookPartId) => {
                    paragraphByBookPartId.set(bookPartId, paragraph);
                });
            });

            this.renderItems.forEach((item) => {
                if (item.type === 'list-line') {
                    let currentParagraph = null;
                    let currentListItem = null;

                    item.ids.forEach((bookPartId) => {
                        const paragraph = paragraphByBookPartId.get(bookPartId);
                        if (!paragraph) return;

                        if (paragraph !== currentParagraph) {
                            currentParagraph = paragraph;
                            currentListItem = { ...item, ids: [] };
                            paragraph.items.push(currentListItem);
                        }

                        currentListItem.ids.push(bookPartId);
                    });
                    return;
                }

                const paragraph = paragraphByBookPartId.get(item.id);
                if (paragraph) {
                    paragraph.items.push(item);
                }
            });

            return paragraphs.filter((paragraph) => paragraph.items.length);
        },
    },

    created() {
        this.showStyleRules = JSON.parse(localStorage.getItem('menuBarItem-stylesheet'));

        window.addEventListener(`menuBarItem-stylesheet-switched`, ({ detail }) => {
            this.showStyleRules = detail.content;
        });
    },

    methods: {
        isPause,
        isListTextPart,
        isListPausePart,

        getListLineStyle(item) {
            return getListIndentStyle(item.listLevel);
        },

        showBookpart(id) {
            const bp = this.page.bookparts[id];

            const cond = [bp.type_part.slug === 'text'];

            if (this.customerSettings && this.customerSettings.settings) {
                if (this.customerSettings.settings.hideTitle) {
                    cond.push(bp.type_text.slug !== 'book_name');
                }

                if (this.customerSettings.settings.hideAuthor) {
                    cond.push(bp.type_text.slug !== 'author');
                }
            }

            return cond.every((c) => !!c);
        },

        showPause(id) {
            const bp = this.page.bookparts[id];
            const cond = [isPause(bp)];

            if (this.customerSettings && this.customerSettings.settings) {
                const idx = this.page.bookpartsList.indexOf(id);
                const [prevIdx, nextIdx] = [idx - 1, idx + 1];

                let prevBp, nextBp;

                if (prevIdx > -1) {
                    prevBp = this.page.bookparts[this.page.bookpartsList[prevIdx]];
                }

                if (nextIdx < this.page.bookpartsList.length - 1) {
                    nextBp = this.page.bookparts[this.page.bookpartsList[nextIdx]];
                }

                if (this.customerSettings.settings.hideTitle) {
                    cond.push(
                        prevBp &&
                            prevBp.type_text.slug !== 'book_name' &&
                            nextBp &&
                            nextBp.type_text.slug !== 'book_name'
                    );
                }

                if (this.customerSettings.settings.hideAuthor) {
                    cond.push(
                        prevBp &&
                            prevBp.type_text.slug !== 'author' &&
                            nextBp &&
                            nextBp.type_text.slug !== 'author'
                    );
                }
            }

            return cond.every((c) => !!c);
        },

        onMouseEnter() {
            if (this.active === this.page.id) return;

            this.setActivePage(this.page.id);
        },

        onPlayPart(event) {
            this.$emit('playPart', event);
        },
    },
};
</script>

<style src="./EditorTextPage.scss" lang="scss"></style>
